import React from "react";
import { Helmet } from "react-helmet-async";

const BASE_URL = "https://thesterlingtouch.com.au";

/**
 * SEO component — sets <title>, meta description, OpenGraph, geo tags,
 * and JSON-LD (LocalBusiness, optional FAQPage + BreadcrumbList) per page.
 */
const SEO = ({
  title,
  description,
  path = "/",
  image,
  suburb, // when provided, includes LocalBusiness schema for that area
  noindex = false,
  faqs, // optional [{q, a}] → FAQPage rich results
  breadcrumbs, // optional [{name, path}] → BreadcrumbList rich results
}) => {
  const url = `${BASE_URL}${path}`;
  const ogImage = image || `${BASE_URL}/og-image.jpg`;

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "HouseCleaningService",
    name: "The Sterling Touch",
    image: ogImage,
    url,
    telephone: "+61 413 996 908",
    email: "hello@thesterlingtouch.com.au",
    description,
    areaServed: suburb
      ? {
          "@type": "Place",
          name: `${suburb}, Brisbane, QLD, Australia`,
        }
      : {
          "@type": "City",
          name: "Brisbane, QLD, Australia",
        },
    address: {
      "@type": "PostalAddress",
      addressLocality: "Brisbane",
      addressRegion: "QLD",
      addressCountry: "AU",
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "08:30",
        closes: "17:00",
      },
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: "Saturday",
        opens: "09:00",
        closes: "15:00",
      },
    ],
    priceRange: "$$$",
  };

  const faqSchema =
    faqs && faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : null;

  const breadcrumbSchema =
    breadcrumbs && breadcrumbs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((b, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: b.name,
            item: `${BASE_URL}${b.path}`,
          })),
        }
      : null;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta
        name="robots"
        content={
          noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"
        }
      />

      {/* Geo targeting */}
      <meta name="geo.region" content="AU-QLD" />
      <meta name="geo.placename" content={suburb ? `${suburb}, Brisbane` : "Brisbane"} />

      {/* OpenGraph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_AU" />
      <meta property="og:site_name" content="The Sterling Touch" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
